import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import {
  getTipologieRifiutoProdotto,
  updateTipologiaRifiutoProdotto,
  type TipologiaRifiutoProdotto,
} from '../../api/settings';
import { useAuthStore } from '../../store/authStore';
import { canWriteSettings } from '../../lib/constants';

/**
 * LISTINO DI VENDITA per tipologia di rifiuto prodotto (V78).
 *
 * STA QUI, accanto ai prezzi dei conferimenti, e non nella scheda «Tipologie»:
 * chi cerca a quanto vendiamo l'alluminio FV va dove già cerca quanto facciamo
 * pagare i pannelli. La scheda Tipologie resta l'anagrafica — quali materiali
 * esistono sotto quale CER — e questa è la tabella dei prezzi.
 *
 * DUE COLONNE IN MENO del blocco dei conferimenti, e sono assenze volute:
 *  - niente DIREZIONE: le vendite hanno un verso solo;
 *  - niente REGIME IVA: sta sul CER rifiuto prodotto ed è una qualificazione
 *    fiscale del MATERIALE, non un prezzo. Metterlo anche qui vorrebbe dire
 *    due posti da cui scrivere la stessa cosa.
 *
 * SOLO LE TIPOLOGIE ATTIVE (`solo_attive`): un materiale che non si può più
 * produrre non è un listino. L'anagrafica, dove si (dis)attiva, è l'altra
 * scheda.
 *
 * Il prezzo PROPONE, non vincola: entra sulla riga di fattura quando la riga
 * nasce, e in bozza resta correggibile. Se manca, in bozza il campo è vuoto e
 * si digita — l'emissione non si blocca per un listino assente.
 */

const inputCls = 'w-full text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white disabled:bg-stone-bg disabled:text-ink-soft disabled:cursor-not-allowed disabled:border-stone-mid';

function toDraft(t: TipologiaRifiutoProdotto): string {
  // `null` → campo VUOTO, mai «0»: uno zero scritto sembra un prezzo deciso, e
  // il backend lo rifiuta (`gt=0`). «Nessun prezzo» ha già il suo valore.
  return t.prezzo_default != null ? String(t.prezzo_default) : '';
}

export default function PrezziVenditaSettings() {
  const { t } = useTranslation();
  const role = useAuthStore((s) => s.user?.role);
  // Scrittura = chi già scrive il catalogo delle tipologie. NON il gate fiscale:
  // un prezzo di listino non cambia aliquota, Natura né dicitura.
  const canWrite = canWriteSettings(role);
  const [items, setItems] = useState<TipologiaRifiutoProdotto[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);

  const reload = useCallback(() => {
    void (async () => {
      setLoading(true);
      try {
        const data = await getTipologieRifiutoProdotto(true);
        setItems(data);
        setDrafts(Object.fromEntries(data.map((x) => [x.id, toDraft(x)])));
      } catch {
        setError(t('settings.prezzi_vendita.errore_caricamento'));
      } finally {
        setLoading(false);
      }
    })();
  }, [t]);
  useEffect(() => { reload(); }, [reload]);

  // Il timer del badge «salvato» si spegne allo smontaggio: un setTimeout vivo
  // oltre il confine del componente fa uscire vitest con 1.
  useEffect(() => {
    if (!savedId) return;
    const h = setTimeout(() => setSavedId(null), 3000);
    return () => clearTimeout(h);
  }, [savedId]);

  async function save(item: TipologiaRifiutoProdotto) {
    const grezzo = (drafts[item.id] ?? '').trim();
    // Campo vuoto = nessuna modifica inviata, come la tariffa dei conferimenti
    // nel blocco qui sopra: il PATCH ignora i campi assenti, quindi un prezzo
    // già messo non si può riportare a «nessuno» svuotando l'input.
    const prezzo = grezzo === '' ? undefined : Number(grezzo);
    if (prezzo != null && (Number.isNaN(prezzo) || prezzo <= 0)) {
      setError(t('settings.prezzi_vendita.val_prezzo'));
      return;
    }
    setSavingId(item.id); setError(null); setSavedId(null);
    try {
      const updated = await updateTipologiaRifiutoProdotto(item.id, { prezzo_default: prezzo });
      setItems((list) => list.map((x) => (x.id === item.id ? updated : x)));
      setDrafts((d) => ({ ...d, [item.id]: toDraft(updated) }));
      setSavedId(item.id);
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('settings.prezzi_vendita.errore_salvataggio'));
    } finally {
      setSavingId(null);
    }
  }

  return (
    <div className="space-y-5 bg-white border border-stone-mid rounded-xl p-5" data-testid="prezzi-vendita">
      <p className="text-sm text-ink-mid">
        {t('settings.prezzi_vendita.intro')}
        <span className="text-ink-soft"> {t('settings.prezzi_vendita.intro_dettaglio')}</span>
      </p>

      {error && <div className="p-3 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger" data-testid="prezzi-vendita-error">{error}</div>}

      {loading ? (
        <div className="text-center py-6 text-sm text-ink-soft">{t('settings.prezzi_vendita.caricamento')}</div>
      ) : items.length === 0 ? (
        <div className="text-center py-6 text-sm text-ink-soft" data-testid="prezzi-vendita-vuoto">
          {t('settings.prezzi_vendita.vuoto')}
        </div>
      ) : (
        /* NIENTE `table-fixed`: a layout automatico nessuna colonna può finire a
           zero, qualunque sia il contenuto. È lo stesso difetto che ha schiacciato
           DESCRIZIONE nella tabella dei CER rifiuti prodotti. */
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-stone-mid text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">
              <th className="py-2">{t('settings.prezzi_vendita.col_tipologia')}</th>
              <th className="py-2">{t('settings.prezzi_vendita.col_cer')}</th>
              <th className="py-2">{t('settings.prezzi_vendita.col_prezzo')}</th>
              {canWrite && <th className="py-2 text-right">{t('settings.prezzi_vendita.col_azioni')}</th>}
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-b border-stone-mid/60" data-testid={`prezzo-vendita-row-${item.id}`}>
                <td className="py-2 pr-3 font-medium text-ink-dark">{item.nome}</td>
                <td className="py-2 pr-3 text-ink-mid">
                  {item.nome_descrittivo ? `${item.cer} — ${item.nome_descrittivo}` : item.cer}
                </td>
                <td className="py-2 pr-3">
                  <input
                    type="number" step="0.0001" min="0" className={inputCls}
                    data-testid={`prezzo-vendita-input-${item.id}`}
                    placeholder={t('settings.prezzi_vendita.placeholder')}
                    value={drafts[item.id] ?? ''}
                    disabled={!canWrite}
                    onChange={(e) => setDrafts((d) => ({ ...d, [item.id]: e.target.value }))}
                  />
                </td>
                {canWrite && (
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      onClick={() => save(item)}
                      disabled={savingId === item.id}
                      data-testid={`prezzo-vendita-save-${item.id}`}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-sage rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-60 inline-flex items-center gap-1"
                    >
                      {savedId === item.id ? <><Check size={13} /> {t('settings.prezzi_vendita.salvato')}</>
                        : savingId === item.id ? t('settings.prezzi_vendita.salvataggio')
                        : t('settings.prezzi_vendita.salva')}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
